// Pure helpers for the Daily Predictions view: response parsing (the backend's
// shapes are unreliable, so everything is narrowed here), rashi lookup, lucky
// values and IST date/time formatting.

export const IST = 'Asia/Kolkata';

/* ------------------------------------------------------------------ */
/* Rashi                                                               */
/* ------------------------------------------------------------------ */

export type RashiName =
  | 'Aries'
  | 'Taurus'
  | 'Gemini'
  | 'Cancer'
  | 'Leo'
  | 'Virgo'
  | 'Libra'
  | 'Scorpio'
  | 'Sagittarius'
  | 'Capricorn'
  | 'Aquarius'
  | 'Pisces';

export interface RashiInfo {
  en: RashiName;
  devanagari: string;
  glyph: string;
  aliases: string[];
}

export const RASHI_INFO: Record<RashiName, RashiInfo> = {
  Aries: { en: 'Aries', devanagari: 'मेष', glyph: '♈', aliases: ['mesha', 'mesh'] },
  Taurus: { en: 'Taurus', devanagari: 'वृषभ', glyph: '♉', aliases: ['vrishabha', 'vrishabh'] },
  Gemini: { en: 'Gemini', devanagari: 'मिथुन', glyph: '♊', aliases: ['mithuna', 'mithun'] },
  Cancer: {
    en: 'Cancer',
    devanagari: 'कर्क',
    glyph: '♋',
    aliases: ['karkataka', 'karka', 'kark'],
  },
  Leo: { en: 'Leo', devanagari: 'सिंह', glyph: '♌', aliases: ['simha', 'singh'] },
  Virgo: { en: 'Virgo', devanagari: 'कन्या', glyph: '♍', aliases: ['kanya'] },
  Libra: { en: 'Libra', devanagari: 'तुला', glyph: '♎', aliases: ['tula'] },
  Scorpio: {
    en: 'Scorpio',
    devanagari: 'वृश्चिक',
    glyph: '♏',
    aliases: ['vrishchika', 'vrishchik'],
  },
  Sagittarius: { en: 'Sagittarius', devanagari: 'धनु', glyph: '♐', aliases: ['dhanu', 'dhanur'] },
  Capricorn: { en: 'Capricorn', devanagari: 'मकर', glyph: '♑', aliases: ['makara', 'makar'] },
  Aquarius: {
    en: 'Aquarius',
    devanagari: 'कुंभ',
    glyph: '♒',
    aliases: ['kumbha', 'kumbh', 'कुम्भ'],
  },
  Pisces: { en: 'Pisces', devanagari: 'मीन', glyph: '♓', aliases: ['meena', 'meen'] },
};

const RASHI_LOOKUP: Map<string, RashiName> = new Map(
  Object.values(RASHI_INFO).flatMap((r) =>
    [r.en, r.devanagari, ...r.aliases].map((name) => [name.toLowerCase(), r.en] as const)
  )
);

/**
 * Maps any spelling of a moon sign (any case, English, Hindi/Sanskrit or
 * Devanagari, or a combined label like "Vrishchika / Scorpio") to the English
 * name the API expects. Returns null when it can't be recognised.
 */
export function normalizeRashi(value: unknown): RashiName | null {
  if (typeof value !== 'string') return null;
  const parts = [value, ...value.split(/[/,()]/)];
  for (const part of parts) {
    const match = RASHI_LOOKUP.get(part.trim().toLowerCase());
    if (match) return match;
  }
  return null;
}

/** Reads the moon sign from the user object the Users table passes in router state. */
export function getUserMoonSign(user: unknown): RashiName | null {
  if (!isRecord(user)) return null;
  const meta = isRecord(user.meta) ? user.meta : undefined;
  const birthDetail = isRecord(user.BirthDetail) ? user.BirthDetail : undefined;
  const astroData = isRecord(birthDetail?.astroData) ? birthDetail?.astroData : undefined;
  return normalizeRashi(meta?.moonSign) ?? normalizeRashi(astroData?.sign);
}

/* ------------------------------------------------------------------ */
/* Narrowing                                                           */
/* ------------------------------------------------------------------ */

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function str(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

/* ------------------------------------------------------------------ */
/* Prediction                                                          */
/* ------------------------------------------------------------------ */

export interface DailyPrediction {
  predictionId?: string;
  predictionDate?: string;
  rashi?: string;
  status?: string;
  text?: string;
  luckyNumbers: string[];
  luckyColors: string[];
}

/** Splits "9, 3", [9, 3], ["9","3"], 9 or null into a de-duplicated list. */
export function splitLuckyList(value: unknown): string[] {
  const parts = Array.isArray(value) ? value : value == null ? [] : [value];
  const cleaned = parts
    .filter((part): part is string | number => typeof part === 'string' || typeof part === 'number')
    .flatMap((part) => String(part).split(','))
    .map((part) => part.trim())
    .filter(Boolean);
  return [...new Set(cleaned)];
}

export function parseDailyPrediction(raw: unknown): DailyPrediction {
  const root = isRecord(raw) ? raw : {};
  const body = isRecord(root.prediction) ? root.prediction : {};
  return {
    predictionId: str(root.predictionId),
    predictionDate: str(root.predictionDate),
    rashi: str(root.rashi),
    status: str(root.status),
    text: str(body.prediction),
    luckyNumbers: splitLuckyList(body.luckyNumber),
    luckyColors: splitLuckyList(body.luckyColor),
  };
}

export type PredictionStatus = 'PENDING' | 'FAILED' | 'DRAFT' | 'SUCCESS';

/** Same rules, in the same order, as the Weekly and Monthly tabs. */
export function getPredictionStatus(q: {
  data?: { status?: string };
  isError: boolean;
  isPending: boolean;
}): PredictionStatus {
  const status = q.data?.status?.toUpperCase();
  if (q.isError || status === 'FAILED') return 'FAILED';
  if (status === 'DRAFT') return 'DRAFT';
  if (q.isPending || status === 'PENDING') return 'PENDING';
  return q.data ? 'SUCCESS' : 'FAILED';
}

export function isPendingPrediction(data?: { status?: string }): boolean {
  return data?.status?.toUpperCase() === 'PENDING';
}

/* ------------------------------------------------------------------ */
/* Lucky colours                                                       */
/* ------------------------------------------------------------------ */

const LUCKY_COLOR_HEX: Record<string, string> = {
  'blood red': '#8B0000',
  crimson: '#DC143C',
  scarlet: '#FF2400',
  maroon: '#800000',
  red: '#FF0000',
  coral: '#FF7F50',
  pink: '#FF7EB6',
  rose: '#E8467C',
  magenta: '#D6329B',
  purple: '#8B5CF6',
  violet: '#7C5CFF',
  indigo: '#4B0082',
  'sky blue': '#65C7F7',
  navy: '#1B3A6B',
  blue: '#00AEEF',
  turquoise: '#40E0D0',
  teal: '#12A6A6',
  emerald: '#2ECC71',
  green: '#48C74A',
  olive: '#808000',
  lime: '#9BE826',
  golden: '#FFC83D',
  gold: '#FFD700',
  mustard: '#E1A32B',
  saffron: '#F4A300',
  amber: '#FFBF00',
  orange: '#FF8A2B',
  yellow: '#FFD700',
  bronze: '#CD7F32',
  copper: '#C87533',
  brown: '#A0642A',
  beige: '#E8D9BE',
  cream: '#F5EBD6',
  ivory: '#FFFFF0',
  white: '#FFFFFF',
  silver: '#C7CFD8',
  grey: '#9AA6B4',
  gray: '#9AA6B4',
  black: '#2B2B2B',
};

const LUCKY_COLOR_KEYS = Object.keys(LUCKY_COLOR_HEX).sort((a, b) => b.length - a.length);

/** Swatch colour for a label like "Blood Red / Crimson": the longest known name wins. */
export function resolveLuckyColor(label: string): string {
  const normalized = label.toLowerCase();
  const match = LUCKY_COLOR_KEYS.find((key) => normalized.includes(key));
  return (match && LUCKY_COLOR_HEX[match]) || '#9FB4CC';
}

/* ------------------------------------------------------------------ */
/* Panchang & yogas                                                    */
/* ------------------------------------------------------------------ */

export interface VaraYoga {
  id?: string;
  weekday?: string;
  nakshatra?: string;
  yoga: string;
  from?: string;
  to?: string;
}

export interface DailyPanchang {
  date?: string;
  sunrise?: string;
  sunset?: string;
  tithi?: string;
  nakshatra?: string;
  yoga?: string;
  amantaMonth?: string;
  paksha?: string;
  abhijitMuhurat: string[];
  amritKalam: string[];
  rahuKalam: string[];
  gulikaKalam: string[];
  yamaganda: string[];
  durMuhurtam: string[];
  varjyam: string[];
  varaYoga: VaraYoga[];
}

export interface SpecialYoga {
  id?: string;
  yoga: string;
  combination?: string;
  startTime?: string;
  endTime?: string;
  period?: string;
}

/** A timing can be "08:45 PM - 10:17 PM", an array of such strings, or null. */
export function toWindows(value: unknown): string[] {
  const list: unknown[] = Array.isArray(value) ? value : value ? [value] : [];
  return list
    .filter((w): w is string => typeof w === 'string')
    .map((w) => w.trim())
    .filter(Boolean);
}

/** Dur Muhurtam items are strings or { start, end }. */
function toDurMuhurtam(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((d: unknown) => {
      if (typeof d === 'string') return d.trim();
      if (isRecord(d)) {
        const start = str(d.start);
        const end = str(d.end);
        return start && end ? `${start} – ${end}` : '';
      }
      return '';
    })
    .filter(Boolean);
}

/** tithi / nakshatra / yoga: prefer displayName, fall back to name. */
function panchangName(value: unknown): string | undefined {
  if (!isRecord(value)) return str(value);
  return str(value.displayName) ?? str(value.name);
}

const AUSPICIOUS_VARA_YOGAS = new Set(['amrita', 'siddha', 'amritasiddha', 'a', 'si', 'asi']);
const INAUSPICIOUS_VARA_YOGAS = new Set(['utpata', 'marana', 'prabalarishta', 'u', 'm', 'pra']);

export type YogaNature = 'auspicious' | 'inauspicious' | 'unknown';

/**
 * Nature of a Nakshatra-Vara yoga from its name, ignoring case, the word "Yoga"
 * and non-letters, so "Utpata", "Utpata Yoga" and the short code "U" all match.
 */
export function varaYogaNature(yoga?: string): YogaNature {
  const key = (yoga ?? '').toLowerCase().replace(/yoga|[^a-z]/g, '');
  if (AUSPICIOUS_VARA_YOGAS.has(key)) return 'auspicious';
  if (INAUSPICIOUS_VARA_YOGAS.has(key)) return 'inauspicious';
  return 'unknown';
}

export function parsePanchang(raw: unknown): DailyPanchang {
  const p = isRecord(raw) ? raw : {};
  const auspicious = isRecord(p.auspiciousTimings) ? p.auspiciousTimings : {};
  const inauspicious = isRecord(p.inauspiciousTimings) ? p.inauspiciousTimings : {};
  const calendar = isRecord(p.calendar) ? p.calendar : {};

  const durMuhurtam = Array.isArray(inauspicious.durMuhurtam)
    ? inauspicious.durMuhurtam
    : p.durMuhurtam;

  const varaYoga: VaraYoga[] = Array.isArray(p.varaYoga)
    ? p.varaYoga.flatMap((v: unknown) => {
        if (!isRecord(v)) return [];
        const yoga = str(v.yoga);
        if (!yoga) return [];
        return [
          {
            id: str(v.id),
            weekday: str(v.weekday),
            nakshatra: str(v.nakshatra),
            yoga,
            from: str(v.from),
            to: str(v.to),
          },
        ];
      })
    : [];

  return {
    date: str(p.date),
    sunrise: str(p.sunrise),
    sunset: str(p.sunset),
    tithi: panchangName(p.tithi),
    nakshatra: panchangName(p.nakshatra),
    yoga: panchangName(p.yoga),
    amantaMonth: str(calendar.amantaMonth) ?? str(calendar.amanta_month),
    paksha: str(calendar.paksha),
    abhijitMuhurat: toWindows(auspicious.abhijitMuhurat),
    amritKalam: toWindows(auspicious.amritKalam),
    rahuKalam: toWindows(inauspicious.rahuKalam),
    gulikaKalam: toWindows(inauspicious.gulikaKalam),
    yamaganda: toWindows(inauspicious.yamaganda),
    durMuhurtam: toDurMuhurtam(durMuhurtam),
    varjyam: toWindows(p.varjyam),
    varaYoga,
  };
}

export function parseSpecialYogas(raw: unknown): SpecialYoga[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((y: unknown) => {
    if (!isRecord(y)) return [];
    const yoga = str(y.yoga);
    if (!yoga) return [];
    return [
      {
        id: str(y.id),
        yoga,
        combination: str(y.combination),
        startTime: str(y.startTime),
        endTime: str(y.endTime),
        period: str(y.period),
      },
    ];
  });
}

/* ------------------------------------------------------------------ */
/* Dates & IST formatting                                              */
/* ------------------------------------------------------------------ */

/**
 * Today's date in the browser's own timezone, as YYYY-MM-DD. An admin in
 * Seattle at 9 PM on 28 Oct asks for 28 Oct, not India's 29 Oct.
 */
export function todayLocal(now: Date = new Date()): string {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function isValidDate(d: Date): boolean {
  return !Number.isNaN(d.getTime());
}

export function istTime(d: Date): string {
  return d.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: IST,
  });
}

function istDay(d: Date): string {
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: IST });
}

function istDateKey(d: Date): string {
  return d.toLocaleDateString('en-CA', { timeZone: IST });
}

/** An ISO timestamp as an IST clock time ("06:16 AM"); the input as-is if unparseable. */
export function formatIstClock(iso: string): string {
  const d = new Date(iso);
  return isValidDate(d) ? istTime(d) : iso;
}

/** IST range; the date is added only when start and end fall on different days. */
export function formatTimeRange(start?: string, end?: string): string {
  if (!start || !end) return [start, end].filter(Boolean).join(' – ') || 'N/A';

  const s = new Date(start);
  const e = new Date(end);
  if (!isValidDate(s) || !isValidDate(e)) return `${start} – ${end}`;

  return istDateKey(s) === istDateKey(e)
    ? `${istTime(s)} – ${istTime(e)}`
    : `${istDay(s)}, ${istTime(s)} – ${istDay(e)}, ${istTime(e)}`;
}

/**
 * "2026-10-06" → "Tue, 6 October 2026". A bare date parses as UTC midnight, so
 * it is read back in UTC; local getters would show the previous day west of UTC.
 */
export function formatDisplayDate(ymd: string): string {
  const d = new Date(ymd.slice(0, 10));
  if (!isValidDate(d)) return ymd;
  const parts = new Intl.DateTimeFormat('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).formatToParts(d);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';
  return `${get('weekday')}, ${get('day')} ${get('month')} ${get('year')}`;
}

/**
 * Splits one timing window into its time and an optional date suffix, collapsing
 * a date repeated on both ends: "03:56 AM (25 SEP) - 05:35 AM (25 SEP)" →
 * { time: "03:56 AM – 05:35 AM", date: "(25 SEP)" }.
 */
export function splitTimingWindow(value: string): { time: string; date?: string } {
  let cleaned: string;
  const duplicate = value.match(/^(.+?)\s*\(([^)]+)\)\s*[-–]\s*(.+?)\s*\(\2\)$/i);
  if (duplicate) {
    const [, start = '', dateStr = '', end = ''] = duplicate;
    cleaned = `${start.trim()} – ${end.trim()} (${dateStr.trim()})`;
  } else {
    cleaned = value.replace(/\s*-\s*/g, ' – ');
  }

  const trailing = cleaned.match(/^(.+?)\s*(\([^)]+\))$/);
  if (trailing) {
    const [, time = '', date = ''] = trailing;
    return { time, date };
  }
  return { time: cleaned };
}

/** Prediction text split into paragraphs on blank lines. */
export function toParagraphs(text?: string): string[] {
  if (!text) return [];
  return text
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
}
